import { TriggerApi } from './TriggerApi';
import {
  BaseApiClient,
  ListTriggerRequestBody,
  ListTriggerResponse,
  RunTriggerResponse,
  RunTriggerOptions,
  RunTriggerRequestBody,
  TriggerListItem,
  ListTriggerOptions,
} from '@internal/backstage-plugin-cloudbuild-common';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export class TriggerClient extends BaseApiClient implements TriggerApi {
  private readonly cache = new Map<
    string,
    { timestamp: number; response: any }
  >();
  private readonly CACHE_DURATION = 5 * 60 * 1000;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    super(options.discoveryApi, options.fetchApi);
  }

  private async fetchWithCache<T>(
    key: string,
    fetcher: () => Promise<T>,
  ): Promise<T> {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.response as T;
    }
    const response = await fetcher();
    this.cache.set(key, { timestamp: Date.now(), response });
    return response;
  }

  async listTriggers(options: ListTriggerOptions): Promise<TriggerListItem[]> {
    const requestBody: ListTriggerRequestBody = {
      projectId: options.projectId,
      location: options.location,
      repoName: options.repoName,
      entityRef: options.entityRef,
    };

    return this.fetchWithCache(
      `listTriggers-${JSON.stringify(requestBody)}`,
      async () => {
        const response = await this.post<ListTriggerResponse>(
          'trigger/list',
          requestBody,
        );
        return response.triggers || [];
      },
    );
  }

  invalidateCache(): void {
    this.cache.clear();
  }

  async runTrigger(options: RunTriggerOptions): Promise<RunTriggerResponse> {
    const requestBody: RunTriggerRequestBody = {
      projectId: options.projectId,
      triggerId: options.triggerId,
      location: options.location,
      source: options.source,
      substitutions: options.substitutions,
      entityRef: options.entityRef,
    };

    const result = await this.post<RunTriggerResponse>(
      'trigger/run',
      requestBody,
    );
    this.invalidateCache();
    return result;
  }
}
